import { Alert, Box, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IMinUserDepricated, IProjectDeprecated } from "@mctypes/global.types";
import { IMCBIMAPI } from "@mctypes/mcbim.types";
import { IconPlayerPlay } from "@tabler/icons-react";
import React from "react";
import { FailedAlert, MCLoader } from "../../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { MCBIM_VIEWER_CACHE_TAG, mcbimViewerApi } from "../../../../../mcbimViewerApi.enhance";
import { useCreateMcBimJobMutation, useGetListOfMcBimJobsQuery } from "../McBimRTK";
import { isMcBimAdmin } from "../mcBimSlice";
import McBimSingleJob from "./McBimSingleJob";
import McJobTable from "./McJobTable";

const McBimJobbPage: React.FC<{ mcbimProject: IMCBIMAPI.Project; project: IProjectDeprecated }> = ({
  mcbimProject,
  project,
}) => {
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.globalReducer.user);
  const userIsMcBimAdmin = isMcBimAdmin(project.mcbim.admins as IMinUserDepricated[], user?._id);

  const {
    data: mcbimJobs,
    isLoading: mcbimJobsLoading,
    error: errorGettingBimJobs,
    refetch: fetchJobs,
  } = useGetListOfMcBimJobsQuery(project._id);

  const [createJob, { isLoading: createJobLoading, error: createJobError }] = useCreateMcBimJobMutation();

  const jobInQueOrInProgress = mcbimJobs?.find((job) => job.status === "queued" || job.status === "in-progress");

  const startJob = async () => {
    await createJob(project._id);
    fetchJobs();
    setModalOpen(false);
  };

  const handleJobComplete = React.useCallback(() => {
    fetchJobs();
    dispatch(mcbimViewerApi.util.invalidateTags([MCBIM_VIEWER_CACHE_TAG]));
  }, [dispatch, fetchJobs]);

  const isLoading = createJobLoading === true || mcbimJobsLoading === true;
  const isError = errorGettingBimJobs != null || createJobError != null;

  return (
    <Box w="100%" h="100%" p="sm" style={{ overflow: "auto" }}>
      {isLoading === true && <MCLoader loading />}
      {isError === true && <FailedAlert />}
      {jobInQueOrInProgress != null && (
        <McBimSingleJob jobId={jobInQueOrInProgress.jobId} projectId={project._id} onComplete={handleJobComplete} />
      )}
      {isLoading === false && isError == false && jobInQueOrInProgress == null && (
        <>
          {userIsMcBimAdmin === true && (
            <Button color="green" leftSection={<IconPlayerPlay size={16} />} onClick={() => setModalOpen(true)}>
              Starte synkronisering til MCBIM
            </Button>
          )}

          {modalOpen === true && (
            <StartJobModal
              startJob={startJob}
              toggle={() => setModalOpen(false)}
              nrOfFiles={mcbimProject?.ifcFiles?.length}
            />
          )}
          <McJobTable jobs={mcbimJobs} projectId={project._id} />
        </>
      )}
    </Box>
  );
};

export default McBimJobbPage;

const StartJobModal: React.FC<{ toggle: () => void; nrOfFiles: number; startJob: () => void }> = ({
  toggle,
  nrOfFiles,
  startJob,
}) => {
  const [isJobStarted, setIsJobStarted] = React.useState<boolean>(false);
  const initJob = () => {
    setIsJobStarted(true);
    startJob();
  };

  return (
    <Modal opened onClose={toggle} title={nrOfFiles === 0 ? "Ingen filer er valgt" : "Vil du starte utvinning?"}>
      {nrOfFiles === 0 ? (
        <Alert color="yellow">Det er ingen filer valgt for å starte utvinning.</Alert>
      ) : (
        <Text>{nrOfFiles} filer valgt for utvinning til MCBIM</Text>
      )}
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={toggle}>
          Avbryt
        </Button>
        <Button disabled={nrOfFiles === 0 || isJobStarted === true} color="blue" onClick={initJob}>
          Starte
        </Button>
      </Group>
    </Modal>
  );
};
